/**
 * Forms & lead capture model. Field definitions and behavior settings live
 * as jsonb on public.forms; the public page renders them and submits
 * through the anon-granted submit_form RPC (validation, contact
 * find-or-create, attribution, consent grants, deal/task/sequence side
 * effects all happen server-side).
 */

export interface FormFieldDef {
  key: string;
  label?: string;
  type:
    | "text"
    | "email"
    | "phone"
    | "textarea"
    | "select"
    | "checkbox"
    | "hidden"
    | "consent_email"
    | "consent_sms";
  required?: boolean;
  placeholder?: string;
  options?: string[];
}

export interface FormSettings {
  success_message?: string;
  redirect_url?: string | null;
  create_deal?: boolean;
  deal_stage?: string;
  deal_name_template?: string;
  create_task?: boolean;
  task_text_template?: string;
  assigned_to?: number | null;
  sequence_id?: string | null;
  default_source?: string;
  landing?: {
    headline?: string;
    subtext?: string;
    bullets?: string[];
  };
}

export interface LeadForm {
  id: string;
  workspace_id: string;
  slug: string;
  name: string;
  description: string | null;
  fields: FormFieldDef[];
  settings: FormSettings;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FormSubmission {
  id: string;
  workspace_id: string;
  form_id: string;
  contact_id: number | null;
  deal_id: number | null;
  data: Record<string, string>;
  utm: Record<string, string>;
  created_at: string;
}

export const FIELD_TYPE_CHOICES: { id: FormFieldDef["type"]; name: string }[] =
  [
    { id: "text", name: "Text" },
    { id: "email", name: "Email" },
    { id: "phone", name: "Phone" },
    { id: "textarea", name: "Long text" },
    { id: "select", name: "Dropdown" },
    { id: "checkbox", name: "Checkbox" },
    { id: "consent_email", name: "Email consent" },
    { id: "consent_sms", name: "SMS consent" },
    { id: "hidden", name: "Hidden" },
  ];

export const DEFAULT_FORM_FIELDS: FormFieldDef[] = [
  { key: "name", label: "Your name", type: "text", required: true },
  { key: "email", label: "Email", type: "email", required: true },
  { key: "phone", label: "Phone", type: "phone" },
  { key: "message", label: "How can we help?", type: "textarea" },
];

export const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

/** UTM + attribution values captured from the public page's query string. */
export const captureUtm = (search: string): Record<string, string> => {
  const params = new URLSearchParams(search);
  const utm: Record<string, string> = {};
  for (const key of [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ]) {
    const value = params.get(key);
    if (value) utm[key] = value;
  }
  if (document.referrer) utm.referrer = document.referrer;
  utm.landing_page = window.location.pathname + window.location.hash;
  return utm;
};
